import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import {
    Box,
    TextField,
    ListItem,
    ListItemButton,
    Checkbox,
    ListItemText,
    Typography,
    CircularProgress,
} from "@mui/material";
import { searchRegions } from "../../redux/slices/regions";
import { PlatformContext } from "./AddPromotions";

/**
 * RegionList component fetches and displays a list of regions
 *
 * @param {Function} changeHandler - Callback function to handle region selection.
 * @param {Object} inputValue - Object/Array containing the currently selected region details.
 */
export default function RegionList({
    changeHandler = () => { },
    inputValue = {},
    setAnchorEl,
}) {
    const dispatch = useDispatch();
    const platform = useContext(PlatformContext);
    const [filter, setFilter] = useState({
        skip: 0,
        limit: 10,
        searchTerm: "",
    });
    const [isLoading, setIsLoading] = useState(true);
    const [total, setTotal] = useState(0);

    const [regionsList, setRegionsList] = useState([]);

    const observer = useRef();

    const lastPostElementRef = useCallback(
        (node) => {
            if (isLoading || regionsList.length >= total) return;
            if (observer.current) observer.current.disconnect();
            const { skip, limit, searchTerm } = filter;

            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        const newSkip = skip + limit;
                        setIsLoading(true);

                        dispatch(
                            searchRegions({
                                skip: newSkip,
                                limit,
                                platform: platform,
                                searchTerm: searchTerm || "",
                            })
                        )
                            .unwrap()
                            .then((res) => {
                                const { data, total: newTotal } = res;
                                const newRegions = data?.map((item) => ({
                                    regionId: item?.regionId,
                                    regionName: item?.regionName,
                                }));
                                setRegionsList((prev) => [...prev, ...newRegions]);
                                setTotal(newTotal);
                                setFilter((prev) => ({
                                    ...prev,
                                    skip: newSkip,
                                }));
                            })
                            .finally(() => setIsLoading(false));
                    }
                },
                {
                    root: null,
                    rootMargin: "100px",
                    threshold: 0.1,
                }
            );

            if (node) observer.current.observe(node);
        },
        [isLoading, regionsList.length, total, filter, dispatch]
    );

    const fetchRegionsData = (searchTerm = "", skip = 0) => {
        setIsLoading(true);
        dispatch(
            searchRegions({
                skip: skip,
                limit: filter?.limit,
                platform: platform,
                searchTerm: searchTerm || "",
            })
        )
            .unwrap()
            .then((res) => {
                setIsLoading(false);
                const { data, total } = res;
                const regionData = data?.map((item) => {
                    return {
                        regionId: item?.regionId,
                        regionName: item?.regionName,
                    };
                });
                setTotal(total);
                if (skip === 0) {
                    setRegionsList(regionData);
                } else {
                    setRegionsList((prev) => [...prev, ...regionData]);
                }
            })
            .catch(() => {
                setIsLoading(false);
            });
    }

    useEffect(() => {
        fetchRegionsData();
    }, [dispatch, platform]);

    const Row = ({ index, style }) => {
        const region = regionsList[index];

        const isChecked = Array.isArray(inputValue)
            ? inputValue.some((item) => item?.regionId === region?.regionId)
            : inputValue?.regionId === region?.regionId;

        return (
            <ListItem
                key={region?.regionId}
                disablePadding
                style={style}
                ref={regionsList.length === index + 1 ? lastPostElementRef : null}
            >
                <ListItemButton
                    onClick={() => {
                        if (Array.isArray(inputValue)) {
                            if (!isChecked) {
                                changeHandler({ ruleValue: [...inputValue, region] });
                            } else {
                                changeHandler({
                                    ruleValue: inputValue.filter((item) => item?.regionId !== region?.regionId),
                                });
                            }
                        } else {
                            setAnchorEl(null);
                            changeHandler({
                                ruleValue: isChecked ? inputValue : region,
                            });
                        }
                    }}
                    sx={{
                        p: 0,
                        bgcolor: (_theme) => (isChecked ? "action.selected" : "inherit"),
                    }}
                    className="popover-btn-area"
                >
                    {(Array.isArray(inputValue)) && (
                        <Checkbox
                            edge="start"
                            checked={isChecked}
                            tabIndex={-1}
                            disableRipple
                            onClick={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                    if (!isChecked) {
                                        changeHandler({ ruleValue: [...inputValue, region] });
                                    }
                                } else {
                                    changeHandler({
                                        ruleValue: inputValue.filter((item) => item?.regionId !== region?.regionId),
                                    });
                                }
                            }}
                            style={{ margin: 0 }}
                        />
                    )}

                    <ListItemText
                        primary={
                            <Typography variant="body2" color="#2E2E3A" fontWeight="semibold">
                                {region.regionName}
                            </Typography>
                        }
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    return (
        <Box
            sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
            }}
        >
            {/* Search Box */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search regions..."
                    onChange={debounce((e) => {
                        const searchValue = e.target.value;
                        setFilter((prev) => ({ ...prev, searchTerm: searchValue, skip: 0 }));
                        fetchRegionsData(searchValue, 0);
                    }, 500)}
                />
            </Box>


            {/* Empty State */}
            {!isLoading && regionsList?.length === 0 && (
                <Box sx={{ p: 3, transform: "scale(0.9)" }}>
                    Data Not Found
                </Box>
            )}

            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                {regionsList.map((_, index) => (
                    <Row key={regionsList[index]?.regionId} index={index} />
                ))}
            </Box>
            {/* Loading State */}
            <>
                {isLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress size={24} color="primary" />
                    </Box>
                )}{" "}
            </>
        </Box>
    );
}
